import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Send, Ban, CreditCard, Upload, X, FileText, RefreshCw } from "lucide-react";

const REVISED_TYPES = ["application/pdf", "image/png", "image/jpeg"];

const RevisedReportField = ({ requestId, file, onChange, label = "Versione corretta del report (facoltativa) · PDF, PNG, JPG" }) => {
  const inputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [preview, setPreview] = useState(null);
  useEffect(() => {
    if (!file || !file.type.startsWith("image/")) { setPreview(null); return undefined; }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);
  const pick = (f) => {
    if (!f) return;
    if (!REVISED_TYPES.includes(f.type)) { toast.error("Formato non supportato: usa PDF, PNG o JPG"); return; }
    if (f.size > 25 * 1024 * 1024) { toast.error("File troppo grande (max 25 MB)"); return; }
    setLoading(true);
    setTimeout(() => { onChange(f); setLoading(false); }, 400);
  };
  return (
    <div data-testid={`work-revised-field-${requestId}`} className="mt-3 border-t border-[#C5A059]/40 pt-3"
      onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => { e.preventDefault(); setDragging(false); pick(e.dataTransfer.files?.[0]); }}>
      <p className="text-[10px] uppercase tracking-[0.2em] text-[#8A6A2B] mb-2">{label}</p>
      <input ref={inputRef} type="file" accept=".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg" className="hidden" data-testid={`work-revised-input-${requestId}`}
        onChange={(e) => { pick(e.target.files?.[0]); e.target.value = ""; }} />
      {loading ? (
        <div data-testid={`work-revised-loading-${requestId}`} className="flex items-center gap-2 text-xs text-inksoft h-12"><Loader2 className="w-4 h-4 animate-spin text-[#C5A059]" />Caricamento del file…</div>
      ) : file ? (
        <div data-testid={`work-revised-selected-${requestId}`} className="flex items-center gap-3 bg-white border border-ink/15 rounded-sm p-2">
          {preview ? <img src={preview} alt="" data-testid={`work-revised-preview-${requestId}`} className="w-14 h-14 object-contain bg-cream rounded-sm" /> : <div className="w-14 h-14 flex items-center justify-center bg-cream rounded-sm"><FileText className="w-6 h-6 text-[#8A6A2B]" /></div>}
          <div className="min-w-0 flex-1">
            <p data-testid={`work-revised-name-${requestId}`} className="text-xs font-semibold text-ink truncate">{file.name}</p>
            <p className="text-[11px] text-inksoft">{(file.size / 1024 / 1024).toFixed(2)} MB · {file.type === "application/pdf" ? "PDF" : "Immagine"}</p>
          </div>
          <button type="button" data-testid={`work-revised-replace-${requestId}`} onClick={() => inputRef.current?.click()} className="inline-flex items-center gap-1 h-8 px-2 text-[10px] uppercase tracking-wide border border-ink/20 rounded-sm text-ink"><RefreshCw className="w-3 h-3" />Sostituisci</button>
          <button type="button" data-testid={`work-revised-remove-${requestId}`} onClick={() => onChange(null)} aria-label="Rimuovi file" className="inline-flex items-center justify-center h-8 w-8 border border-red-700/30 text-red-700 rounded-sm"><X className="w-3.5 h-3.5" /></button>
        </div>
      ) : (
        <button type="button" data-testid={`work-revised-pick-${requestId}`} onClick={() => inputRef.current?.click()} className={`w-full border border-dashed hover:border-[#C5A059] rounded-sm px-4 py-3 flex items-center justify-center gap-2 text-xs font-semibold uppercase tracking-[0.12em] text-ink transition-colors ${dragging ? "border-[#C5A059] bg-[#C5A059]/10" : "border-ink/25 bg-white"}`}>
          <Upload className="w-4 h-4 text-[#C5A059]" />Carica o trascina qui il file
        </button>
      )}
    </div>
  );
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const uploadDeliveryFile = async (requestId, file) => {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await axios.post(`${API}/admin/requests/${requestId}/delivery-file`, fd, { withCredentials: true });
  return data;
};
export const WORK_STATUS = {
  received: { label: "Ricevuta", cls: "bg-sand text-inksoft" },
  in_progress: { label: "In lavorazione", cls: "bg-[#C5A059]/15 text-[#8A6A2B]" },
  delivered: { label: "Consegnata", cls: "bg-sage/20 text-sagedark" },
  canceled: { label: "Annullata", cls: "bg-red-50 text-red-700" },
};
const PAY = {
  authorized: { label: "Carta pre-autorizzata — nessun addebito", cls: "bg-[#C5A059]/15 text-[#8A6A2B]" },
  paid: { label: "Pagamento incassato", cls: "bg-sage/20 text-sagedark" },
  canceled: { label: "Autorizzazione rilasciata", cls: "bg-red-50 text-red-700" },
  refunded: { label: "Rimborsato", cls: "bg-red-50 text-red-700" },
  pending: { label: "Pagamento non completato", cls: "bg-sand text-inksoft" },
  demo: { label: "Demo — nessun pagamento, report di esempio", cls: "bg-[#EEE6D6] text-[#8A6A2B]" },
};

export const WorkStatusBadge = ({ status, testid }) => {
  const s = WORK_STATUS[status] || WORK_STATUS.received;
  return <span data-testid={testid} className={`text-[10px] uppercase tracking-[0.2em] font-semibold rounded-sm px-3 py-1.5 ${s.cls}`}>{s.label}</span>;
};

export const PaymentBadge = ({ payment, testid }) => {
  if (!payment) return null;
  const p = PAY[payment.payment_status] || { label: payment.payment_status, cls: "bg-sand text-inksoft" };
  const expires = payment.authorized_at && payment.payment_status === "authorized" ? new Date(new Date(payment.authorized_at).getTime() + 7 * 864e5) : null;
  return (
    <span data-testid={testid} className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] font-semibold rounded-sm px-3 py-1.5 ${p.cls}`}>
      <CreditCard className="w-3 h-3" />{p.label}{payment.amount ? ` · € ${Number(payment.amount).toFixed(2)}` : ""}
      {expires && <span className="normal-case tracking-normal font-normal">· scade {expires.toLocaleDateString("it-IT")}</span>}
    </span>
  );
};

export const WorkStatusPanel = ({ request, onSaved }) => {
  const [status, setStatus] = useState(request.work_status || "received");
  const [notes, setNotes] = useState(request.internal_notes || "");
  const [message, setMessage] = useState("");
  const [revised, setRevised] = useState(null);
  const [busy, setBusy] = useState("");
  const [extraFile, setExtraFile] = useState(null);
  const [extraMessage, setExtraMessage] = useState("");
  const closed = request.work_status === "delivered" || request.work_status === "canceled";
  const sentFiles = (request.delivery_files || []).filter((f) => f.sent_at);
  const authorized = request.payment?.payment_status === "authorized";

  const save = async () => {
    setBusy("save");
    try {
      const { data } = await axios.patch(`${API}/admin/requests/${request.id}/work`, { work_status: status, internal_notes: notes }, { withCredentials: true });
      toast.success("Stato e note salvati");
      onSaved?.({ ...request, work_status: data.work_status, internal_notes: data.internal_notes });
    } catch {
      toast.error("Errore nel salvataggio");
    } finally {
      setBusy("");
    }
  };

  const approve = async () => {
    if (!window.confirm(`Approvare e inviare il report a ${request.email}?${revised ? ` Il cliente riceverà il link per scaricare "${revised.name}".` : ""}${authorized ? " Il pagamento verrà acquisito ora." : ""}`)) return;
    setBusy("approve");
    try {
      const uploaded = revised ? await uploadDeliveryFile(request.id, revised) : null;
      const { data } = await axios.post(`${API}/admin/requests/${request.id}/approve`, { delivery_message: message, file_path: uploaded?.storage_path }, { withCredentials: true });
      toast.success(data.captured ? "Report inviato al cliente e pagamento acquisito" : "Report inviato al cliente");
      const now = new Date().toISOString();
      onSaved?.({ ...request, work_status: "delivered", delivered_at: now, delivery_files: uploaded ? [...(request.delivery_files || []), { ...uploaded, sent_at: now }] : request.delivery_files, payment: request.payment ? { ...request.payment, payment_status: data.payment_status || request.payment.payment_status } : request.payment });
      setMessage("");
      setRevised(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nell'approvazione");
    } finally {
      setBusy("");
    }
  };

  const sendFile = async () => {
    if (!extraFile) return;
    if (!window.confirm(`Inviare "${extraFile.name}" a ${request.email}? Nessuna operazione sul pagamento.`)) return;
    setBusy("send-file");
    try {
      const uploaded = await uploadDeliveryFile(request.id, extraFile);
      const { data } = await axios.post(`${API}/admin/requests/${request.id}/send-file`, { file_path: uploaded.storage_path, message: extraMessage }, { withCredentials: true });
      toast.success("File inviato al cliente");
      onSaved?.({ ...request, delivery_files: [...(request.delivery_files || []), { ...uploaded, sent_at: data.sent_at }] });
      setExtraFile(null);
      setExtraMessage("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nell'invio del file");
    } finally {
      setBusy("");
    }
  };

  const cancel = async () => {
    if (!window.confirm(`Annullare la richiesta di ${request.name}?${authorized ? " L'autorizzazione verrà rilasciata, nessun addebito al cliente." : ""}`)) return;
    setBusy("cancel");
    try {
      const { data } = await axios.post(`${API}/admin/requests/${request.id}/cancel`, {}, { withCredentials: true });
      toast.success(data.released ? "Richiesta annullata, autorizzazione rilasciata" : "Richiesta annullata");
      onSaved?.({ ...request, work_status: "canceled", payment: request.payment ? { ...request.payment, payment_status: data.refunded ? "refunded" : data.released ? "canceled" : request.payment.payment_status } : request.payment });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nell'annullamento");
    } finally {
      setBusy("");
    }
  };

  return (
    <div data-testid={`work-panel-${request.id}`} className="mt-5 border-t border-ink/10 pt-5 grid md:grid-cols-[auto_1fr] gap-5">
      <div>
        <p className="text-[10px] uppercase tracking-[0.2em] text-inksoft mb-2">Stato lavorazione</p>
        <div className="flex gap-1.5" role="radiogroup">
          {["received", "in_progress"].map((k) => (
            <button key={k} type="button" data-testid={`work-status-${k}-${request.id}`} aria-checked={status === k} role="radio" disabled={closed} onClick={() => setStatus(k)}
              className={`text-[10px] uppercase tracking-[0.15em] font-semibold px-3 py-2 rounded-sm border transition-colors disabled:opacity-50 ${status === k ? "border-ink bg-ink text-cream" : "border-ink/15 text-inksoft hover:border-ink/40"}`}>
              {WORK_STATUS[k].label}
            </button>
          ))}
        </div>
        {request.delivered_at && <p data-testid={`work-delivered-at-${request.id}`} className="mt-2 text-[11px] text-sagedark">Consegnata il {new Date(request.delivered_at).toLocaleString("it-IT")}</p>}
        {request.canceled_at && <p data-testid={`work-canceled-at-${request.id}`} className="mt-2 text-[11px] text-red-700">Annullata il {new Date(request.canceled_at).toLocaleString("it-IT")}</p>}
      </div>
      <div className="space-y-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.2em] text-inksoft mb-2">Note interne (visibili solo allo studio)</p>
          <textarea data-testid={`work-notes-${request.id}`} value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder="Appunti, scadenze, decisioni prese con il cliente…"
            className="w-full text-sm p-3 bg-cream border border-ink/15 rounded-sm focus:outline-none focus:border-ink" />
        </div>
        {!closed && (
          <div data-testid={`work-delivery-box-${request.id}`} className="bg-[#F7F5F0] border border-[#C5A059]/50 rounded-sm p-3">
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#8A6A2B] mb-2">Messaggio personale nell'email di consegna (facoltativo)</p>
            <textarea data-testid={`work-delivery-message-${request.id}`} value={message} onChange={(e) => setMessage(e.target.value)} rows={3}
              placeholder={`Gentile ${request.name}, è stato un piacere disegnare la luce del tuo spazio…`}
              className="w-full text-sm p-3 bg-white border border-ink/15 rounded-sm focus:outline-none focus:border-ink" />
            <RevisedReportField requestId={request.id} file={revised} onChange={setRevised} />
            <p className="mt-2 text-[11px] text-inksoft">Con "Approva e invia al cliente" il cliente ({request.email}) riceve l'email "Progetto consegnato"{revised ? " con il link per scaricare il file caricato" : ""}{authorized ? " e il pagamento pre-autorizzato viene acquisito nello stesso momento" : ""}.</p>
          </div>
        )}
        {request.work_status === "delivered" && (
          <div data-testid={`work-send-file-box-${request.id}`} className="bg-[#F7F5F0] border border-[#C5A059]/50 rounded-sm p-3">
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#8A6A2B] mb-2">Invia un file al cliente (facoltativo)</p>
            <textarea data-testid={`work-send-file-message-${request.id}`} value={extraMessage} onChange={(e) => setExtraMessage(e.target.value)} rows={2}
              placeholder={`Gentile ${request.name}, ecco la versione aggiornata del tuo progetto…`}
              className="w-full text-sm p-3 bg-white border border-ink/15 rounded-sm focus:outline-none focus:border-ink" />
            <RevisedReportField requestId={request.id} file={extraFile} onChange={setExtraFile} label="File da inviare · PDF, PNG, JPG (max 25 MB)" />
            <button type="button" data-testid={`work-send-file-${request.id}`} onClick={sendFile} disabled={!!busy || !extraFile}
              className="mt-3 inline-flex items-center gap-2 h-9 px-4 text-[10px] font-semibold uppercase tracking-[0.15em] bg-ink text-cream hover:bg-[#C5A059] hover:text-ink transition-colors rounded-sm disabled:opacity-50">
              {busy === "send-file" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}Invia file al cliente
            </button>
            <p className="mt-2 text-[11px] text-inksoft">Il cliente ({request.email}) riceve un'email con il link per scaricare il file. Nessuna operazione sul pagamento.</p>
          </div>
        )}
        {sentFiles.length > 0 && (
          <div data-testid={`work-sent-files-${request.id}`} className="text-[11px] text-inksoft">
            <p className="text-[10px] uppercase tracking-[0.2em] mb-1">File inviati al cliente</p>
            {sentFiles.map((f) => (
              <p key={f.storage_path}><a href={`${API}/files/${f.storage_path}`} target="_blank" rel="noreferrer" className="text-[#8A6A2B] underline">{f.original_name}</a> · {new Date(f.sent_at).toLocaleString("it-IT")}</p>
            ))}
          </div>
        )}
        <div className="flex flex-wrap gap-2">
          <button type="button" data-testid={`work-save-${request.id}`} onClick={save} disabled={!!busy || closed}
            className="inline-flex items-center gap-2 h-9 px-4 text-[10px] font-semibold uppercase tracking-[0.15em] border border-ink/20 text-ink rounded-sm disabled:opacity-50">
            {busy === "save" && <Loader2 className="w-3.5 h-3.5 animate-spin" />}Salva stato e note
          </button>
          {!closed && (
            <>
              <button type="button" data-testid={`work-approve-${request.id}`} onClick={approve} disabled={!!busy}
                className="inline-flex items-center gap-2 h-9 px-4 text-[10px] font-semibold uppercase tracking-[0.15em] bg-ink text-cream hover:bg-[#C5A059] hover:text-ink transition-colors rounded-sm disabled:opacity-60">
                {busy === "approve" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}Approva e invia al cliente
              </button>
              <button type="button" data-testid={`work-cancel-${request.id}`} onClick={cancel} disabled={!!busy}
                className="inline-flex items-center gap-2 h-9 px-4 text-[10px] font-semibold uppercase tracking-[0.15em] border border-red-700/40 text-red-700 hover:bg-red-50 transition-colors rounded-sm disabled:opacity-60">
                {busy === "cancel" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Ban className="w-3.5 h-3.5" />}Annulla
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
